import type {
  ProfileUserDataChainCardProps,
  ProfileUserDataChainsTabProps,
} from './ProfileUserDataChainsTab.types';
import type { ReactElement } from 'react';

import { Box, Card, DataList, Flex, Grid, Heading, Skeleton } from '@rango-dev/whale';
import { Pagination } from '@rango-dev/whale/lab';
import BigNumber from 'bignumber.js';
import React from 'react';
import { useTranslation } from 'react-i18next';

import usePagination from '../../../../hooks/usePagination';
import { usePaginationLabels } from '../../../../hooks/usePaginationLabels';
import { useWindowDimensions } from '../../../../hooks/useWindowDimensions';
import { numberToString } from '../../../../utils/Numbers';
import { Image } from '../../../common/Image';
import { CardFieldItem } from '../ProfileUserDataCard';
import { ProfileUserDataEmptyState } from '../ProfileUserDataEmptyState';

import styles from './ProfileUserDataChainsTab.module.css';

const LOGO_SIZE = 24;
const SKELETON_LOGO_SIZE = '30px';
const SKELETON_TITLE_WIDTH = '110px';
const SKELETON_TITLE_HEIGHT = '24px';
const SKELETON_FIELD_COUNT = 6;
const CHAIN_GRID_MIN_WIDTH = '300px';
const CHAIN_LOADING_COUNT = 8;

export function ProfileUserDataChainCard(props: ProfileUserDataChainCardProps): ReactElement {
  const { t } = useTranslation();

  if ('loading' in props) {
    return (
      <Card variant="surface" size={{ initial: '2', md: '3' }}>
        <Flex direction="column" gap="2">
          <Flex align="center" gap="2">
            <Skeleton
              width={SKELETON_LOGO_SIZE}
              height={SKELETON_LOGO_SIZE}
              className={styles.logo}
            />
            <Skeleton width={SKELETON_TITLE_WIDTH} height={SKELETON_TITLE_HEIGHT} />
          </Flex>
          <DataList.Root size="2">
            {Array.from({ length: SKELETON_FIELD_COUNT }, (_, idx) => idx).map((idx) => (
              <CardFieldItem key={`chain-field-skeleton-${idx}`} loading />
            ))}
          </DataList.Root>
        </Flex>
      </Card>
    );
  }

  const { blockchainData } = props;

  const fields = [
    {
      id: 'numOfSwaps',
      label: t('Num of Your Swaps'),
      value: blockchainData.swapCount,
    },
    {
      id: 'numOfBridge',
      label: t('Num of Your Bridges'),
      value: blockchainData.bridgeCount,
    },
    {
      id: 'totalVolume',
      label: t('Total Volume'),
      value: '$' + numberToString(new BigNumber(blockchainData.volume)),
    },
    {
      id: 'popularToken',
      label: t('Your Popular Token'),
      value: blockchainData.favoriteToken?.symbol,
    },
    {
      id: 'favoriteBadge',
      label: t('Your Favorite Bridge'),
      value: blockchainData.favoriteBridge?.title,
    },
    {
      id: 'favoriteDex',
      label: t('Your Favorite DEX'),
      value: blockchainData.favoriteDex?.title,
    },
  ];

  return (
    <Card variant="surface" size={{ initial: '2', md: '3' }}>
      <Flex direction="column" gap="5">
        <Flex align="center" gap="2">
          <Image
            src={blockchainData.blockchain.logo}
            width={LOGO_SIZE}
            height={LOGO_SIZE}
            className={styles.logo}
            alt={blockchainData.blockchain.displayName}
          />
          <Heading size="3" weight="bold">
            {blockchainData.blockchain.displayName}
          </Heading>
        </Flex>
        <DataList.Root size="2">
          {fields.map((field) => (
            <CardFieldItem key={field.id} label={field.label} value={field?.value} />
          ))}
        </DataList.Root>
      </Flex>
    </Card>
  );
}

export function ProfileUserDataChainsTab(props: ProfileUserDataChainsTabProps): ReactElement {
  const { profileChains, loading } = props;
  const { isMobile } = useWindowDimensions();
  const { paginatedItems, totalPages, page, setPage } = usePagination({
    items: profileChains ?? [],
  });
  const paginationLabels = usePaginationLabels();

  if (loading) {
    return (
      <Grid
        columns={{ initial: '1', sm: `repeat(auto-fill, minmax(${CHAIN_GRID_MIN_WIDTH}, 1fr))` }}
        gap={{ initial: '4', md: '5' }}>
        {Array.from({ length: CHAIN_LOADING_COUNT }, (_, idx) => idx).map((idx) => (
          <ProfileUserDataChainCard key={`chain-card-skeleton-${idx}`} loading />
        ))}
      </Grid>
    );
  }

  if (!profileChains?.length) {
    return <ProfileUserDataEmptyState error={props.error} />;
  }

  const itemToRender = isMobile ? profileChains : paginatedItems;

  return (
    <Flex direction="column" gap="5">
      <Grid
        columns={{ initial: '1', sm: `repeat(auto-fill, minmax(${CHAIN_GRID_MIN_WIDTH}, 1fr))` }}
        gap={{ initial: '4', md: '5' }}>
        {itemToRender.map((profileChainItem) => (
          <ProfileUserDataChainCard
            key={profileChainItem.blockchain.blockchain}
            blockchainData={profileChainItem}
          />
        ))}
      </Grid>
      {!isMobile && totalPages > 1 ? (
        <Box>
          <Pagination.Set
            showEdgePages
            currentPage={page}
            totalPages={totalPages}
            onPageChange={setPage}
            labels={paginationLabels}
          />
        </Box>
      ) : null}
    </Flex>
  );
}
